import { LibraryAdd } from '@mui/icons-material';
import { Alert, Avatar, Box, Breadcrumbs, Button, Grid, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { Page } from '../../../design/page/Page';
import { useRequester } from '../../../hooks';
import { EikenMaterialData, EikenMaterialResource } from '../../../resources';
import { EikenMaterialManualForm, FormData } from './components/EikenMaterialManualForm';

type FormValues = FormData & { id?: string };

function validateFormValues(value: FormValues) {
  const hasTitle = value.title && value.title.trim().length > 0;
  const hasPronunciation = value.pronunciation && value.pronunciation.trim().length > 0;
  const hasImageText = value.imageText && value.imageText.trim().length > 0;
  const hasPronunciationQuestion =
    value.pronunciationQuestion && value.pronunciationQuestion.trim().length > 0;
  const hasQ1 = value.q1 && value.q1.trim().length > 0;
  const hasQ2 = value.q2 && value.q2.trim().length > 0;
  const hasQ3 = value.q3 && value.q3.trim().length > 0;
  const hasQ4 = value.q4 && value.q4.trim().length > 0;

  return (
    hasTitle &&
    hasPronunciation &&
    hasImageText &&
    hasPronunciationQuestion &&
    hasQ1 &&
    hasQ2 &&
    hasQ3 &&
    hasQ4
  );
}

export function EikenMaterialCreateManual() {
  const { user } = useAuth();
  const testInitials =
    (user?.data.firstName?.charAt(0) || '') + (user?.data.lastName?.charAt(0) || '');
  const [searchParams] = useSearchParams();
  const count = searchParams.get('count') || 1;
  const [activeTab, setActiveTab] = useState(0);
  const [failedTests, setFailedTests] = useState<number[]>([]);

  const navigate = useNavigate();
  const saveMaterials = useRequester<any>({
    onSubmit: async (validTests: FormValues[]) => {
      const createdTests: EikenMaterialData[] = [];
      for (const test of validTests) {
        const formData: Partial<EikenMaterialData> = {
          referenceTextTitle: test.title,
          createdAt: new Date().toISOString(),
          referenceText: test.pronunciationQuestion,
          imageText: test.imageText,
          content: {
            q1: test.q1,
            q2: test.q2,
            q3: test.q3,
            q4: test.q4,
            pronunciation: test.pronunciation,
          },
        };
        const createdTest = test.id
          ? await EikenMaterialResource.updateOne(test.id, formData)
          : await EikenMaterialResource.createOne(formData);
        createdTests.push(createdTest.data);
      }
      return createdTests;
    },
    onSuccess: () => navigate(`/eiken-materials`),
    onError: console.log,
  });

  const [formValues, setFormValues] = useState<FormValues[]>(
    Array.from({ length: Number(count) }, () => ({
      title: '',
      pronunciation: '',
      imageText: '',
      pronunciationQuestion: '',
      q1: '',
      q2: '',
      q3: '',
      q4: '',
    }))
  );

  const handleFormChange = (index: number, value: FormData | undefined, id?: string) => {
    setFormValues((prev) => {
      const newValues = [...prev];
      if (value) {
        newValues[index] = { ...newValues[index], ...value };
      } else {
        newValues[index] = { ...newValues[index], id };
      }
      return newValues;
    });
  };

  const handleSaveAllTests = () => {
    const failedIndexes: number[] = [];
    formValues.forEach((test, index) => {
      const isValid = validateFormValues(test);
      if (!isValid) {
        failedIndexes.push(index);
      }
    });
    if (failedIndexes.length > 0) {
      setFailedTests(failedIndexes);
      return;
    }
    return saveMaterials.submit(formValues);
  };

  return (
    <Page>
      <Page.Header sx={{ height: 'auto', pt: 4, pb: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Link to="/eiken-materials">
            <Typography color="text.secondary" variant="body2">
              &lt; Back to questions
            </Typography>
          </Link>
        </Stack>
        <div className="flex justify-between">
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <Avatar
              sx={{
                bgcolor: 'border.primary',
                width: 56,
                height: 56,
                fontSize: 24,
                fontWeight: 600,
              }}
            >
              {testInitials}
            </Avatar>
            <div>
              <Breadcrumbs aria-label="breadcrumb">
                <Link className="underline" color="inherit" to="/eiken-materials">
                  Questions
                </Link>
                <Typography color="text.primary">Create Test</Typography>
              </Breadcrumbs>
              <Typography variant="h3" sx={{ fontWeight: 600 }}>
                Create test
              </Typography>
            </div>
          </Stack>
          <div>
            <Button
              variant="contained"
              color="primary"
              startIcon={<LibraryAdd />}
              onClick={handleSaveAllTests}
            >
              <span>Save all tests</span>
            </Button>
          </div>
        </div>
      </Page.Header>
      <Page.Content>
        {failedTests.length > 0 && (
          <Grid container spacing={2} sx={{ maxWidth: 900, mx: 'auto', mt: 3 }}>
            <Alert className="w-full" severity="error">
              Some tests have missing fields. Please fix the errors in the tests before saving.
            </Alert>
          </Grid>
        )}
        <Grid container spacing={2} sx={{ maxWidth: 900, mx: 'auto', mt: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {Array.from({ length: Number(count) }, (_, index) => (
              <Button
                key={index}
                onClick={() => setActiveTab(index)}
                variant={activeTab === index ? 'contained' : 'text'}
                sx={{
                  background:
                    activeTab === index
                      ? '#005d83'
                      : failedTests.includes(index)
                      ? '#ff0000'
                      : '#f5f5f5',
                  color:
                    activeTab === index ? '#fff' : failedTests.includes(index) ? '#fff' : '#000',
                  '&:hover': {
                    color:
                      activeTab === index ? '#fff' : failedTests.includes(index) ? '#000' : '#000',
                  },
                  borderRadius: 100,
                  height: 40,
                  minWidth: 40,
                }}
              >
                {index + 1}
              </Button>
            ))}
          </Box>
        </Grid>
        {Array.from({ length: Number(count) }, (_, index) => (
          <div key={index} className={activeTab === index ? 'block' : 'hidden'}>
            <EikenMaterialManualForm
              eikenMaterial={formValues[index]}
              onFormChange={(value, id) => handleFormChange(index, value, id)}
            />
          </div>
        ))}
      </Page.Content>
    </Page>
  );
}
